import { useState, useEffect } from "react";
import { FONT_MONO } from "theme/fonts";
import { useCookieConsentText } from "./CookieConsent.text";

const CONSENT_KEY = "yogyism_cookie_consent"; // "accepted" | "declined"
const CONSENT_MAX_AGE_DAYS = 180;

function getCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)")
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name: string, value: string, days: number) {
  const maxAge = days * 24 * 60 * 60; // seconds
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${maxAge}; path=/; SameSite=Lax`;
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const t = useCookieConsentText();

  useEffect(() => {
    const existing = getCookie(CONSENT_KEY);
    if (!existing) {
      // slight delay so it doesn't fight with initial page paint/animations
      const t = setTimeout(() => setVisible(true), 400);
      return () => clearTimeout(t);
    }
  }, []);

  const handleChoice = (choice: "accepted" | "declined") => {
    setCookie(CONSENT_KEY, choice, CONSENT_MAX_AGE_DAYS);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t.aria}
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        background: "#040f18",
        borderTop: "1px solid rgba(55,138,221,0.18)",
        boxShadow: "0 -8px 24px rgba(0,0,0,0.45)",
        padding: "16px 20px",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        fontFamily: FONT_MONO,
        animation: "cookieSlideUp 0.35s ease",
      }}
    >
      <style>{`
        @keyframes cookieSlideUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <p style={{
        margin: 0,
        color: "rgba(255,255,255,0.75)",
        fontSize: 12.5,
        lineHeight: 1.5,
        maxWidth: 640,
        flex: "1 1 320px",
      }}>
        {t.body}{" "}
      
      </p>

      <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
        <button
          onClick={() => handleChoice("declined")}
          style={{
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.18)",
            borderRadius: 8,
            padding: "8px 16px",
            fontSize: 12,
            color: "rgba(255,255,255,0.7)",
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          {t.decline}
        </button>
        <button
          onClick={() => handleChoice("accepted")}
          style={{
            background: "#38bdf8",
            border: "none",
            borderRadius: 8,
            padding: "8px 18px",
            fontSize: 12,
            fontWeight: 700,
            color: "#04101c",
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          {t.accept}
        </button>
      </div>
    </div>
  );
}